/**
 * Small inline icon set. Inlined rather than pulled from an icon package so the
 * landing page has no icon-font or icon-library dependency.
 */

import type { ReactElement } from "react";

type IconProps = { className?: string };

const base = "h-6 w-6";
export function ChartIcon({ className = base }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path d="M3 20h18" strokeLinecap="round" />
      <rect x="5" y="11" width="3.2" height="6" rx="1.1" />
      <rect x="10.4" y="7" width="3.2" height="10" rx="1.1" />
      <rect x="15.8" y="13" width="3.2" height="4" rx="1.1" />
    </svg>
  );
}

export function SavingsIcon({ className = base }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path d="M4 15.5 9 10l3.5 3.5L20 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.5 6H20v4.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 19.5h16" strokeLinecap="round" />
    </svg>
  );
}

export function ForecastIcon({ className = base }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path d="M3 17.5c3-1 5-8 8-8s4 5 5.5 5H21" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 20.5h18" strokeLinecap="round" />
      <circle cx="11" cy="9.5" r="1.6" />
    </svg>
  );
}

export function OrgIcon({ className = base }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <rect x="9" y="3" width="6" height="4.5" rx="1.4" />
      <rect x="3" y="16" width="6" height="4.5" rx="1.4" />
      <rect x="15" y="16" width="6" height="4.5" rx="1.4" />
      <path d="M12 7.5V12m0 0H6v4m6-4h6v4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ShieldIcon({ className = base }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path d="M12 3 5 5.8v5.4c0 4.3 2.9 7.6 7 9.8 4.1-2.2 7-5.5 7-9.8V5.8L12 3Z" strokeLinejoin="round" />
      <path d="m9.2 11.8 2 2 3.6-3.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ReportIcon({ className = base }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path d="M6 3h7.5L19 8.5V21H6z" strokeLinejoin="round" />
      <path d="M13.2 3v5.6H19" strokeLinejoin="round" />
      <path d="M9 13h6M9 16.5h6" strokeLinecap="round" />
    </svg>
  );
}

export function CheckIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className={className} aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ArrowRightIcon({ className = "h-4 w-4" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden="true">
      <path d="M4 12h15" strokeLinecap="round" />
      <path d="m13 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MenuIcon({ className = "h-6 w-6" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

export function CloseIcon({ className = "h-6 w-6" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  );
}

export function ChevronIcon({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden="true">
      <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export const featureIcons: Record<string, (p: IconProps) => ReactElement> = {
  chart: ChartIcon,
  savings: SavingsIcon,
  forecast: ForecastIcon,
  org: OrgIcon,
  shield: ShieldIcon,
  report: ReportIcon,
};
